import { useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["awaiting_vendor", "processing", "confirmed", "packed", "shipped", "delivered", "cancelled"];
const errMsg = (e, fallback) => e.response?.data?.detail || fallback;

// Admin-side order actions: details, status update, Shiprocket/pickup retry, cancel, archive/delete.
export default function AdminOrderActions({ order: o, onDone }) {
  const [busy, setBusy] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState(o.status);
  const [tracking, setTracking] = useState("");
  const [confirm, setConfirm] = useState(null); // {kind: "cancel"|"archive"|"delete"}

  const sr = (o.shipments || []).find((s) => s.shipment_id);
  const needsShipment = (!sr || !sr.awb_code) && ["processing", "packed", "confirmed"].includes(o.status) && !o.archived;
  const needsPickup = sr && sr.awb_code && !sr.pickup_requested_at && !["cancelled", "delivered"].includes(o.status);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); toast.success(okMsg); setConfirm(null); setStatusOpen(false); onDone?.(); }
    catch (e) { toast.error(errMsg(e, "Action failed")); }
    finally { setBusy(""); }
  };

  const retryShipment = () => run("ship", () => api.post(`/exhibitor/orders/${o.id}/ship-via-shiprocket`, {}), sr ? "AWB assigned and pickup requested" : "Shiprocket shipment created");
  const retryPickup = () => run("pickup", async () => {
    const { data } = await api.post(`/exhibitor/orders/${o.id}/shipment/pickup`, { pickup_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10) });
    if (!data?.pickup_scheduled) throw new Error("not scheduled");
  }, "Pickup requested");
  const saveStatus = () => run("status", () => api.post(`/exhibitor/orders/${o.id}/status`, { status: newStatus, tracking_number: tracking || undefined, note: "Updated by admin" }), "Status updated");
  const doConfirmed = () => {
    if (confirm.kind === "cancel") return run("cancel", () => api.post(`/exhibitor/orders/${o.id}/status`, { status: "cancelled", note: "Cancelled by admin" }), "Order cancelled");
    if (confirm.kind === "archive") return run("archive", () => api.post(`/admin/orders/${o.id}/archive`), "Order archived");
    if (confirm.kind === "unarchive") return run("archive", () => api.post(`/admin/orders/${o.id}/unarchive`), "Order restored");
    return run("delete", () => api.delete(`/admin/orders/${o.id}`), "Order permanently deleted");
  };

  const btn = "h-7 rounded-full text-[11px] px-3";
  const canCancel = !["cancelled", "delivered"].includes(o.status);
  const confirmText = {
    cancel: "Cancel this order? Stock is returned and any refund flow starts.",
    archive: "Archive this order? It is hidden from the list and excluded from revenue. You can restore it later.",
    unarchive: "Restore this archived order?",
    delete: "Permanently delete this archived order? This cannot be undone.",
  };

  return (
    <div className="flex flex-col gap-1 items-start" data-testid={`order-actions-${o.id}`}>
      <Button variant="outline" className={btn} onClick={() => setDetailsOpen(true)} data-testid={`order-view-${o.id}`}>View details</Button>
      <Button variant="outline" className={btn} onClick={() => { setNewStatus(o.status); setStatusOpen(true); }} data-testid={`order-update-status-${o.id}`}>Update status</Button>
      {needsShipment && <Button className={`${btn} bg-[#F48E2E] hover:bg-[#D97815] text-white`} disabled={!!busy} onClick={retryShipment} data-testid={`order-admin-retry-shipment-${o.id}`}>{busy === "ship" ? "Retrying…" : (sr && !sr.awb_code ? "Retry AWB + pickup" : "Retry Shiprocket")}</Button>}
      {needsPickup && <Button className={`${btn} bg-[#F48E2E] hover:bg-[#D97815] text-white`} disabled={!!busy} onClick={retryPickup} data-testid={`order-admin-retry-pickup-${o.id}`}>{busy === "pickup" ? "Requesting…" : "Request pickup"}</Button>}
      {canCancel && <Button variant="outline" className={`${btn} text-red-600 border-red-200 hover:bg-red-50`} onClick={() => setConfirm({ kind: "cancel" })} data-testid={`order-admin-cancel-${o.id}`}>Cancel order</Button>}
      {o.archived
        ? <>
            <Button variant="outline" className={btn} onClick={() => setConfirm({ kind: "unarchive" })}>Restore</Button>
            <Button variant="outline" className={`${btn} text-red-600 border-red-200 hover:bg-red-50`} onClick={() => setConfirm({ kind: "delete" })} data-testid={`order-delete-${o.id}`}>Delete permanently</Button>
          </>
        : <Button variant="outline" className={`${btn} text-gray-600`} onClick={() => setConfirm({ kind: "archive" })} data-testid={`order-archive-${o.id}`}>Archive</Button>}

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Order {o.id.slice(0, 8).toUpperCase()}</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <div>{o.user_email} · {o.payment_method} · {(o.payment_status || "").replace(/_/g, " ")}</div>
            <div className="text-gray-600">Ship to: {o.address?.full_name}, {o.address?.line1 || o.address?.address_line1 || ""} {o.address?.city} {o.address?.state} {o.address?.pincode} · {o.address?.phone}</div>
            <div className="border rounded-lg divide-y">
              {(o.items || []).map((i, idx) => <div key={idx} className="p-2 flex justify-between"><span>{i.name || i.product_name} × {i.quantity}</span><span>₹{(Number(i.price || 0) * Number(i.quantity || 1)).toLocaleString("en-IN")}</span></div>)}
            </div>
            <div className="text-xs space-y-0.5">
              <div>Product GST ₹{o.product_gst ?? 0} · Packaging GST ₹{o.packaging_gst ?? 0} · Shipping GST ₹{o.shipping_gst ?? 0} · Platform fee ₹{o.platform_fee ?? 0}</div>
              <div className="font-semibold text-sm">Total ₹{Number(o.total || 0).toLocaleString("en-IN")}</div>
            </div>
            {sr && <div className="text-xs">Shiprocket #{sr.shiprocket_order_id} · AWB {sr.awb_code || "pending"} {sr.courier_name ? `· ${sr.courier_name}` : ""}{sr.pickup_requested_at ? ` · pickup requested${sr.pickup_scheduled_date ? ` for ${sr.pickup_scheduled_date}` : ""}` : " · pickup not requested"}{sr.pickup_error ? <div className="text-red-600">{sr.pickup_error}</div> : null}</div>}
            {o.shiprocket_error && <div className="text-xs text-red-600">Shiprocket: {o.shiprocket_error}</div>}
            <div className="text-xs text-gray-500">History: {(o.status_history || []).map((h) => h.status).join(" → ") || "-"}</div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Update status</DialogTitle></DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          {newStatus === "shipped" && !o.tracking_number && <input className="border rounded-md px-3 py-2 text-sm w-full" placeholder="Tracking number (required)" value={tracking} onChange={(e) => setTracking(e.target.value)} />}
          <Button disabled={!!busy || newStatus === o.status} onClick={saveStatus} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">{busy === "status" ? "Saving…" : "Save"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Are you sure?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">{confirm ? confirmText[confirm.kind] : ""}</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirm(null)}>Back</Button>
            <Button disabled={!!busy} onClick={doConfirmed} className="bg-red-600 hover:bg-red-700 text-white" data-testid="order-action-confirm">Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
